import {
	ChangeDetectionStrategy,
	Component,
	EventEmitter,
	OnDestroy,
	OnInit,
	Output,
} from '@angular/core';
import {
	FundingType,
	MatterMortgage,
	MatterMortgageWithAssociations,
} from '@models/mortgage';
import { MortgageTransactionStatus } from '@models/mortgages/mortgage-base';
import { Select } from '@ngxs/store';
import { MatterMortgagesState } from '@state/matter/matter_mortgages';
import { cloneDeep } from 'lodash';
import { BehaviorSubject, Observable, combineLatest } from 'rxjs';
import { Step } from 'src/app/shared/atomic/molecules/step-progress-nav/step-progress-nav.component';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { RequestForFundsSelectors } from '../../closer-sync-rff/request-for-funds.selector';
import { ICON } from '@core/util/icon';
import { openClose } from '@core/animations/animations';
import {
	BusinessMatterClientWithAssociations,
	MatterStatus,
	PersonMatterClientWithAssociations,
} from '@models/matter';
import { MatterClients } from '@state/global/matter_clients';
import { SelectedMatter } from '@state/matter/matter.selectors';
import { SubmitStatus } from '../../closerSync.utils';
import { SelectSnapshot } from '@ngxs-labs/select-snapshot';
import { DetailNavType } from '../../closer-sync-mortgage/closer-sync-mortgage.component';
import { NavigationService } from '@services/nav/nav.service';
const Steps: Step[] = [
	{
		title: 'Funding Details',
	},
	{
		title: 'Request for Funds',
	},
	{
		title: 'Final Report',
	},
	{
		title: 'Completed',
	},
];

enum SELECTED_STEPS {
	FUNDING_DETAILS = 0,
	REQUEST_FOR_FUNDS = 1,
	FINAL_REPORT = 2,
}

const CancelledStatuses = [
	SubmitStatus.CANCELLATION_REQUESTED,
	SubmitStatus.CANCELLED,
];

@UntilDestroy()
@Component({
	selector: 'app-closer-sync-mortgage-header',
	templateUrl: './closer-sync-mortgage-header.component.html',
	changeDetection: ChangeDetectionStrategy.OnPush,
	animations: [openClose()],
	standalone: false,
})
export class CloserSyncMortgageHeaderComponent implements OnInit, OnDestroy {
	public steps = Steps;
	public state: any;
	public fundingType = FundingType;
	public current = 0;
	public _mortgage: MatterMortgageWithAssociations;
	public CloserSyncMortgageStatus = MortgageTransactionStatus;
	public SUBMIT_STATUS = SubmitStatus;

	public ICON = ICON;

	public isOpened = false;

	public SELECTED_STEP = SELECTED_STEPS;

	public selectedStep: number;

	private stepsSubject = new BehaviorSubject<Step[]>(Steps);
	public steps$: Observable<Step[]> = this.stepsSubject.asObservable();

	private currentStepSubject = new BehaviorSubject<number>(1);
	public currentStep$: Observable<number> =
		this.currentStepSubject.asObservable();

	public submitStatusSubject = new BehaviorSubject<SubmitStatus>(
		SubmitStatus.SELECT_FUNDING_DETAILS,
	);
	public submitStatus$: Observable<SubmitStatus> =
		this.submitStatusSubject.asObservable();

	public cancelledSubject = new BehaviorSubject<boolean>(false);
	public cancelled$: Observable<boolean> = this.cancelledSubject.asObservable();

	@SelectSnapshot(SelectedMatter.status) matterStatus: MatterStatus;
	@SelectSnapshot(SelectedMatter.closingDate) closingDate: string;
	@SelectSnapshot(SelectedMatter.id) matterId: string;
	@Select(MatterMortgagesState.getSelectedMortgage)
	mortgage$: Observable<MatterMortgageWithAssociations>;
	@Select(RequestForFundsSelectors.GetValidations)
	validations$: Observable<any>;

	@Output() viewDocuments = new EventEmitter<void>();
	@Output() gotoArea = new EventEmitter<DetailNavType>();

	@Select(MatterClients.get_signorsWithAssociationMapped) matterClients$: (
		| PersonMatterClientWithAssociations
		| BusinessMatterClientWithAssociations
	)[];

	constructor(private navigationService: NavigationService) {}

	ngOnDestroy(): void {
		this.stepsSubject.unsubscribe();
		this.currentStepSubject.unsubscribe();
		this.submitStatusSubject.unsubscribe();
	}

	ngOnInit(): void {
		this.selectedStep = 0;

		combineLatest([this.mortgage$, this.validations$])
			.pipe(untilDestroyed(this))
			.subscribe(([mortgage, validations]) => {
				this._mortgage = mortgage;

				const cancelled = CancelledStatuses.includes(
					this.submitStatusSubject.getValue(),
				);
				this.cancelledSubject.next(cancelled);

				const submitStatus = this.calculateSubmitStatus(mortgage, validations);
				this.submitStatusSubject.next(submitStatus);
			});
	}

	submitRequest(
		submitStatus: SubmitStatus.REQUEST_FOR_FUNDS | SubmitStatus.FINAL_REPORT,
	) {
		this.viewDocuments.emit();
	}

	stepClicked($event: number) {
		switch ($event) {
			case 0:
				this.submitStatusSubject.next(SubmitStatus.SELECT_FUNDING_DETAILS);
				break;
			case 1:
				this.submitStatusSubject.next(SubmitStatus.REQUEST_FOR_FUNDS);
				break;
			case 2:
				this.submitStatusSubject.next(SubmitStatus.FINAL_REPORT);
				break;
			case 3:
				this.submitStatusSubject.next(SubmitStatus.COMPLETED);
		}

		this.currentStepSubject.next($event);
	}

	calculateSubmitStatus(_mortgage: MatterMortgage, _validations?: any) {
		const _steps = cloneDeep(Steps);

		if (!_validations.fundingDetailsValidation?.isValid)
			return SubmitStatus.SELECT_FUNDING_DETAILS;

		switch (_mortgage?.status) {
			case MortgageTransactionStatus.CANCELLATION_REQUESTED:
				this.disabled(_steps);
				return SubmitStatus.CANCELLATION_REQUESTED;
			case MortgageTransactionStatus.CANCELLED:
				this.disabled(_steps);
				return SubmitStatus.CANCELLED;
		}

		if (
			_mortgage?.status === MortgageTransactionStatus.COMPLETED ||
			this.matterStatus !== MatterStatus.ACTIVE
		) {
			this.updateSteps(_steps, 3);
			return SubmitStatus.COMPLETED;
		}

		if (!this.closingDate) return SubmitStatus.REQUEST_FOR_FUNDS;

		const closingDate = new Date(this.closingDate);
		const today = new Date();
		if (closingDate <= today) {
			this.updateSteps(_steps, 1);
			return SubmitStatus.REQUEST_FOR_FUNDS;
		}

		this.updateSteps(_steps, 2);
		return SubmitStatus.FINAL_REPORT;
	}

	private updateSteps(steps: Step[], current: number, failed = false) {
		steps[current].failed = failed;
		this.stepsSubject.next(steps);
		this.currentStepSubject.next(current);
	}

	private disabled(steps: Step[]) {
		for (const step of steps) {
			step.disabled = true;
		}
		this.stepsSubject.next(steps);
		this.currentStepSubject.next(steps.length);
	}

	toggleContainer(): void {
		this.isOpened = !this.isOpened;
	}

	goToDocuments() {
		this.gotoArea.emit(DetailNavType.MORTGAGE_DETAILS);
	}

	reSyncInstructions() {
		this.navigationService.goToMatter_Mortgage_CloserSync_Create(
			this.matterId,
			this._mortgage.relatedMortgageId,
			'existingMatter',
		);
	}
}
